<template>
  <div class="visitor-audit">
    <div class="header">
      <h1 class="header-title">访客审批</h1>
      <p class="header-subtitle">快速查看访客申请，选择通过或驳回</p>
    </div>

    <main class="main">
      <section class="card card-filters">
        <div class="filter-block">
          <div class="filter-label">审批状态</div>
          <div class="seg-row seg-row--status">
            <button
              type="button"
              class="seg-btn seg-btn--solid"
              :class="{ active: auditTab === 'all' }"
              @click="setAuditTab('all')"
            >
              全部审批
            </button>
            <button
              type="button"
              class="seg-btn seg-btn--solid"
              :class="{ active: auditTab === 'pending' }"
              @click="setAuditTab('pending')"
            >
              待审批
            </button>
            <button
              type="button"
              class="seg-btn seg-btn--solid"
              :class="{ active: auditTab === 'done' }"
              @click="setAuditTab('done')"
            >
              已审批
            </button>
          </div>
        </div>
        <div class="filter-block">
          <div class="filter-label">时间范围</div>
          <div class="seg-row seg-row--time">
            <button
              type="button"
              class="seg-btn seg-btn--outline"
              :class="{ active: timeRangeDays === 7 }"
              @click="setTimeRangeDays(7)"
            >
              最近7天
            </button>
            <button
              type="button"
              class="seg-btn seg-btn--outline"
              :class="{ active: timeRangeDays === 15 }"
              @click="setTimeRangeDays(15)"
            >
              最近15天
            </button>
            <button
              type="button"
              class="seg-btn seg-btn--outline"
              :class="{ active: timeRangeDays === 30 }"
              @click="setTimeRangeDays(30)"
            >
              最近30天
            </button>
          </div>
        </div>
      </section>

      <p class="summary-line">{{ summaryLine }}</p>

      <van-pull-refresh
        v-model="refreshing"
        class="audit-pull"
        success-text="刷新成功"
        @refresh="onPullRefresh"
      >
        <van-list
          ref="auditList"
          v-model="listLoading"
          class="audit-list"
          :finished="listFinished"
          :immediate-check="false"
          :finished-text="applications.length ? '没有更多了' : ''"
          @load="onListLoad"
        >
          <section
            v-for="(item, index) in applications"
            :key="'audit-' + item.id + '-' + index"
            class="card card-detail"
          >
            <h2 class="card-title">访客申请详情</h2>
            <p class="card-subtitle">{{ detailSubtitleForItem(item) }}</p>

            <div class="field-row">
              <span class="field-label">预约单号</span>
              <span class="field-value">{{ item.applyNo }}</span>
            </div>
            <div class="field-row">
              <span class="field-label">访客姓名</span>
              <span class="field-value">{{ item.visitorName }}</span>
            </div>
            <div class="field-row">
              <span class="field-label">手机号</span>
              <span class="field-value">{{ item.phone }}</span>
            </div>
            <div class="field-row">
              <span class="field-label">车牌号码</span>
              <span class="field-value">{{ item.plateNo }}</span>
            </div>
            <div class="field-row">
              <span class="field-label">来访事由</span>
              <span class="field-value">{{ item.reason }}</span>
            </div>
            <div class="field-row">
              <span class="field-label">来访时段</span>
              <span class="field-value">{{ item.timeRange }}</span>
            </div>
            <div class="field-row">
              <span class="field-label">被访人</span>
              <span class="field-value">{{ item.host }}</span>
            </div>
            <div class="field-row field-row--status">
              <span class="field-label">当前状态</span>
              <span class="field-value field-value--badge">
                <span class="badge" :class="getStatusBadgeClass(item.status)">{{ getStatusLabel(item.status) }}</span>
              </span>
            </div>
            <div v-if="item.status === 1" class="actions">
              <van-button class="btn btn-danger" @click="onReject(index)">驳回</van-button>
              <van-button class="btn btn-primary" type="info" @click="onApprove(index)">通过</van-button>
            </div>
          </section>
        </van-list>
      </van-pull-refresh>

      <van-empty
        v-if="!listLoading && !refreshing && applications.length === 0 && listFinished"
        description="暂无访客申请"
        class="empty-box"
      />
    </main>
  </div>
</template>

<script>
import { Dialog } from 'vant'
import * as common from '@/api/common'
import { IotService } from '@/api/iotService'

const PAGE_SIZE = 20

export default {
  name: 'VisitorAudit',
  data () {
    return {
      sid: '',
      sceneAccessToken: '',
      /** 列表触底加载（van-list v-model） */
      listLoading: false,
      listFinished: false,
      /** 下一页页码，从 1 开始 */
      nextPageNo: 1,
      /** 下拉刷新 */
      refreshing: false,
      /** 防止列表接口并发（重复页） */
      auditListFetching: false,
      /** 审批状态：全部 | 待审批 | 已审批（已通过/已驳回/超时） */
      auditTab: 'all',
      /** 时间范围天数，默认最近7天 */
      timeRangeDays: 7,
      /** 当前列表 total（接口返回） */
      listTotal: 0,
      /** 全部审批 tab：时间范围内总条数 */
      statsTotalAll: 0,
      /** 全部审批 tab：时间范围内待审批条数（单独请求 total） */
      statsPendingInRange: 0,
      // 状态值：1待审核；2审核通过；3审核超时自动通过；4审核驳回；5审核超时自动驳回
      applications: []
    }
  },
  computed: {
    timeRangeLabel () {
      return `最近${this.timeRangeDays}天内`
    },
    summaryLine () {
      const head = this.timeRangeLabel
      if (this.auditTab === 'all') {
        const total = this.statsTotalAll
        const pend = this.statsPendingInRange
        return `${head} · 共 ${total} 条（其中待审批 ${pend} 条）`
      }
      if (this.auditTab === 'pending') {
        return `${head} · 待审批 ${this.listTotal} 条`
      }
      return `${head} · 已审批（已通过/已驳回）共 ${this.listTotal} 条`
    }
  },
  methods: {
    waitMs (ms) {
      return new Promise(resolve => {
        setTimeout(resolve, ms)
      })
    },
    getStatusBadgeClass (status) {
      if (status === 2 || status === 3) return 'badge-success'
      if (status === 4 || status === 5) return 'badge-danger'
      return 'badge-warning'
    },
    getStatusLabel (status) {
      if (status === 1) return '待审批'
      if (status === 2 || status === 3) return '已通过'
      if (status === 4 || status === 5) return '已驳回'
      return '—'
    },
    detailSubtitleForItem (item) {
      if (item && item.status === 1) {
        return '请核对访客与来访信息，选择通过或驳回。'
      }
      return '以下为申请信息与审批结果。'
    },
    getAuditStatusListByTab (tab) {
      if (tab === 'all') return [1, 2, 3, 4, 5]
      if (tab === 'pending') return [1]
      return [2, 3, 4, 5]
    },
    getTimeRangeByDays (days) {
      const end = new Date()
      end.setHours(23, 59, 59, 999)
      const start = new Date()
      start.setDate(start.getDate() - (days - 1))
      start.setHours(0, 0, 0, 0)
      return {
        startTime: common.fmtDate(start, 'yyyy-MM-dd hh:mm:ss'),
        endTime: common.fmtDate(end, 'yyyy-MM-dd hh:mm:ss')
      }
    },
    formatVisitTimeRange (startStr, endStr) {
      if (!startStr || !endStr) return `${startStr || ''} ～ ${endStr || ''}`
      const sd = startStr.slice(0, 10)
      const ed = endStr.slice(0, 10)
      const st = startStr.slice(11, 16)
      const et = endStr.slice(11, 16)
      if (sd === ed) {
        return `${sd} ${st} ~ ${et}`
      }
      return `${startStr.slice(0, 16)} ~ ${endStr.slice(0, 16)}`
    },
    setAuditTab (tab) {
      if (this.auditTab === tab) return
      this.auditTab = tab
      this.resetListState()
      this.fetchApplicationsPage()
    },
    setTimeRangeDays (days) {
      if (this.timeRangeDays === days) return
      this.timeRangeDays = days
      this.resetListState()
      this.fetchApplicationsPage()
    },
    resetListState () {
      this.applications = []
      this.nextPageNo = 1
      this.listFinished = false
      this.listTotal = 0
      this.statsTotalAll = 0
      this.statsPendingInRange = 0
    },
    mapReservationItem (item) {
      const hostDeptName = item.interviewee_department_name ? `（${item.interviewee_department_name}）` : ''
      const hostName = item.interviewee_full_name || ''
      return {
        id: item.id,
        applyNo: item.reservation_no,
        visitorName: item.full_name,
        phone: item.phone_number,
        plateNo: item.plate_number,
        reason: item.reason,
        timeRange: this.formatVisitTimeRange(item.reservation_start, item.reservation_end),
        host: `${hostName}${hostDeptName}`,
        status: item.audit_status
      }
    },
    onListLoad () {
      this.fetchApplicationsPage()
    },
    async onPullRefresh () {
      this.refreshing = true
      this.resetListState()
      await this.fetchApplicationsPage()
    },
    async ensureVisitorFeatureEnabled () {
      this.sid = (this.$route && this.$route.query && this.$route.query.sid) ? String(this.$route.query.sid) : ''
      if (!this.sid) {
        this.$toast('缺少车场参数 sid')
        return false
      }
      try {
        const featureRes = await IotService.getVisitorFeatureBySid(this.sid)
        if (!IotService.isVisitorFeatureEnabled(featureRes)) {
          this.$router.replace({
            path: '/visitor/unavailable',
            query: { sid: this.sid, type: 'feature' }
          })
          return false
        }
        return true
      } catch (e) {
        this.$toast('访客功能状态获取失败，请稍后重试')
        return false
      }
    },

    /**
     * 从 session / local 读取员工场景 token。
     * 未写入过期时间或过期时间无效时仍使用 token，由服务端校验；避免误清存储导致刷新后首请求无 token。
     */
    resolveEmployeeSceneToken () {
      const now = Date.now()
      const readFrom = (get) => {
        const t = get('employeeSceneAccessToken')
        const expRaw = get('employeeSceneAccessToken_expireAt')
        if (!t) return null
        const exp = expRaw ? parseInt(expRaw, 10) : 0
        if (!Number.isFinite(exp) || exp <= 0) return t
        return now < exp ? t : null
      }
      const fromSess = readFrom(k => sessionStorage.getItem(k))
      if (fromSess) return fromSess
      return readFrom(k => localStorage.getItem(k))
    },

    clearEmployeeSceneStorage () {
      sessionStorage.removeItem('employeeSceneAccessToken')
      sessionStorage.removeItem('employeeSceneAccessToken_expireAt')
      localStorage.removeItem('employeeSceneAccessToken')
      localStorage.removeItem('employeeSceneAccessToken_expireAt')
    },

    redirectToEmployeeLogin () {
      const redirect = `/visitor/audit?sid=${encodeURIComponent(this.sid)}`
      this.$router.replace({ path: '/employee/login', query: { sid: this.sid, redirect } })
    },

    /** 将当前有效 token 写入 this.sceneAccessToken，无则 false */
    applyEmployeeSceneToken () {
      const token = this.resolveEmployeeSceneToken()
      if (!token) {
        this.sceneAccessToken = ''
        return false
      }
      this.sceneAccessToken = token
      return true
    },

    ensureEmployeeAuth () {
      this.sid = (this.$route && this.$route.query && this.$route.query.sid) ? String(this.$route.query.sid) : ''
      if (!this.sid) return false

      const token = this.resolveEmployeeSceneToken()
      if (!token) {
        this.clearEmployeeSceneStorage()
        this.redirectToEmployeeLogin()
        return false
      }

      this.sceneAccessToken = token
      return true
    },

    async fetchApplicationsPage (opts = {}) {
      const silent = opts.silent === true
      if (!this.sid) {
        this.listLoading = false
        return
      }
      if (this.listFinished) {
        this.listLoading = false
        return
      }
      if (this.auditListFetching) {
        this.listLoading = false
        return
      }

      if (!this.applyEmployeeSceneToken()) {
        this.listLoading = false
        this.refreshing = false
        this.clearEmployeeSceneStorage()
        this.redirectToEmployeeLogin()
        return
      }

      const pageNo = this.nextPageNo
      this.listLoading = true
      this.auditListFetching = true

      try {
        const range = this.getTimeRangeByDays(this.timeRangeDays)
        const auditList = this.getAuditStatusListByTab(this.auditTab)
        const accessToken = this.sceneAccessToken

        let res
        let resPending = null

        if (pageNo === 1 && this.auditTab === 'all') {
          res = await IotService.reservationGetListForAudit(accessToken, this.sid, {
            page_no: 1,
            page_size: PAGE_SIZE,
            start_time: range.startTime,
            end_time: range.endTime,
            audit_status_list: auditList
          })
          if (res && res.code === common.resCode.OK) {
            try {
              resPending = await IotService.reservationGetListForAudit(accessToken, this.sid, {
                page_no: 1,
                page_size: 1,
                start_time: range.startTime,
                end_time: range.endTime,
                audit_status_list: [1]
              })
            } catch (e) {
              resPending = null
            }
          }
        } else {
          res = await IotService.reservationGetListForAudit(accessToken, this.sid, {
            page_no: pageNo,
            page_size: PAGE_SIZE,
            start_time: range.startTime,
            end_time: range.endTime,
            audit_status_list: auditList
          })
        }

        if (res && res.code === common.resCode.Unauthorized) {
          this.clearEmployeeSceneStorage()
          this.sceneAccessToken = ''
          if (!silent) {
            this.$toast(common.resMSG[common.resCode.Unauthorized] || '请重新登录')
          }
          this.redirectToEmployeeLogin()
          return
        }

        if (res && res.code === common.resCode.OK) {
          const list = res.list || []
          const mapped = list.map(item => this.mapReservationItem(item))

          if (pageNo === 1) {
            this.applications = mapped
            this.listTotal = res.total != null ? res.total : mapped.length
            if (this.auditTab === 'all' && resPending && resPending.code === common.resCode.OK) {
              this.statsTotalAll = this.listTotal
              this.statsPendingInRange = resPending.total != null ? resPending.total : 0
            } else {
              this.statsTotalAll = 0
              this.statsPendingInRange = 0
            }
          } else {
            this.applications = [...this.applications, ...mapped]
          }

          const pageLen = mapped.length
          const total = res.total != null ? res.total : this.applications.length
          this.listFinished = pageLen < PAGE_SIZE || this.applications.length >= total

          if (!this.listFinished) {
            this.nextPageNo = pageNo + 1
          }
        } else {
          if (pageNo === 1) {
            this.applications = []
            this.listTotal = 0
            this.statsTotalAll = 0
            this.statsPendingInRange = 0
          }
          this.listFinished = true
          if (!silent) {
            this.$toast(res && res.msg ? res.msg : '获取审批列表失败')
          }
        }
      } catch (e) {
        if (pageNo === 1) {
          this.applications = []
          this.listTotal = 0
          this.statsTotalAll = 0
          this.statsPendingInRange = 0
        }
        this.listFinished = true
        if (!silent) {
          this.$toast(e && e.message ? e.message : '获取审批列表失败')
        }
      } finally {
        this.auditListFetching = false
        this.listLoading = false
        this.refreshing = false
      }
    },

    async onApprove (index) {
      const item = this.applications[index]
      if (!item) return

      Dialog.confirm({
        title: '确认通过',
        message: `确定通过访客「${item.visitorName}」的预约申请吗？`
      }).then(async () => {
        if (!this.applyEmployeeSceneToken()) {
          this.clearEmployeeSceneStorage()
          this.redirectToEmployeeLogin()
          return
        }
        let res = await IotService.reservationApproveBySceneToken(this.sceneAccessToken, this.sid, item.id, true)

        // 首次返回要求重试时，等待3秒后自动重试一次（用户无感知）
        if (res && res.code === common.resCode.OK && res.need_wait_try === true) {
          await this.waitMs(3000)
          res = await IotService.reservationApproveBySceneToken(this.sceneAccessToken, this.sid, item.id, false)
        }

        if (res && res.code === common.resCode.OK) {
          this.$set(this.applications[index], 'status', 2)
          this.$toast.success('已通过该访客申请')
          this.resetListState()
          this.fetchApplicationsPage({ silent: true })
        } else {
          this.$toast(res && res.msg ? res.msg : '审批通过失败')
        }
      }).catch(() => {})
    },

    async onReject (index) {
      const item = this.applications[index]
      if (!item) return

      Dialog.confirm({
        title: '确认驳回',
        message: `确定驳回访客「${item.visitorName}」的预约申请吗？`
      }).then(async () => {
        if (!this.applyEmployeeSceneToken()) {
          this.clearEmployeeSceneStorage()
          this.redirectToEmployeeLogin()
          return
        }
        const res = await IotService.reservationRejectBySceneToken(this.sceneAccessToken, this.sid, item.id, '审批驳回')

        if (res && res.code === common.resCode.OK) {
          this.$set(this.applications[index], 'status', 4)
          this.$toast.success('已驳回该访客申请')
          this.resetListState()
          this.fetchApplicationsPage({ silent: true })
        } else {
          this.$toast(res && res.msg ? res.msg : '审批驳回失败')
        }
      }).catch(() => {})
    }
  },
  async created () {
    this.sid = (this.$route && this.$route.query && this.$route.query.sid) ? String(this.$route.query.sid) : ''
    if (!this.sid) {
      this.$toast('缺少车场参数 sid')
      return
    }
    // 先确认员工 token，再校验访客功能，最后拉列表，避免刷新后首屏在无 token 时误请求并提示「请重新登录」
    if (!this.ensureEmployeeAuth()) return
    if (!(await this.ensureVisitorFeatureEnabled())) return
    this.fetchApplicationsPage()
  }
}
</script>

<style lang="scss" scoped>
.visitor-audit {
  min-height: 100vh;
  background: #f5f7fa;
  padding-bottom: calc(24px + env(safe-area-inset-bottom, 0));
}

.header {
  background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
  color: #fff;
  padding: 14px 16px;
  padding-top: calc(14px + env(safe-area-inset-top, 0));
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.25);
}

.header-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.header-subtitle {
  font-size: 13px;
  margin-top: 4px;
  opacity: 0.95;
  margin-bottom: 0;
}

.main {
  max-width: 640px;
  margin: 0 auto;
  padding: 16px;
}

.card-filters {
  padding: 14px 14px 12px;
}

.filter-block + .filter-block {
  margin-top: 16px;
}

.filter-label {
  font-size: 13px;
  color: #606266;
  margin-bottom: 10px;
  text-align: left;
}

.seg-row {
  display: flex;
  gap: 8px;
}

.seg-btn {
  flex: 1;
  min-height: 36px;
  padding: 6px 4px;
  font-size: 13px;
  line-height: 1.3;
  border-radius: 6px;
  cursor: pointer;
  box-sizing: border-box;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.seg-btn--solid {
  background: #fff;
  border: 1px solid #dcdfe6;
  color: #606266;

  &.active {
    background: #409eff;
    border-color: #409eff;
    color: #fff;
  }
}

.seg-btn--outline {
  background: #fff;
  border: 1px solid #dcdfe6;
  color: #606266;

  &.active {
    background: #ecf5ff;
    border-color: #409eff;
    color: #409eff;
  }
}

.summary-line {
  font-size: 12px;
  color: #909399;
  margin: 0 0 12px 0;
  padding: 0 2px;
  line-height: 1.5;
  text-align: left;
}

.audit-pull {
  overflow: visible;
}

.audit-list {
  min-height: 1px;
}

.field-value--badge {
  display: flex;
  justify-content: flex-end;
}

.field-row--status {
  border-bottom: none;
}

.card {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 16px;
}

.card-title {
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 8px 0;
  color: #303133;
  text-align: left;
}

.card-subtitle {
  font-size: 13px;
  color: #909399;
  margin: 0 0 12px 0;
  line-height: 1.5;
  text-align: left;
}

.card-detail .card-subtitle {
  margin-bottom: 16px;
}

.field-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f2f2f2;
  font-size: 14px;

  &:last-of-type {
    border-bottom: none;
  }
}

.field-label {
  font-size: 13px;
  color: #909399;
  flex-shrink: 0;
  margin-right: 12px;
}

.field-value {
  font-size: 14px;
  color: #303133;
  text-align: right;
  word-break: break-all;
}

.badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 1.4;
}

.badge-warning {
  background: #fdf6ec;
  color: #e6a23c;
}

.badge-success {
  background: #f0f9eb;
  color: #67c23a;
}

.badge-danger {
  background: #fef0f0;
  color: #f56c6c;
}

.actions {
  display: flex;
  gap: 10px;
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #ebeef5;

  .btn {
    flex: 1;
    height: 44px;
    border-radius: 8px;
    font-size: 15px;
    border: none;

    &::before {
      display: none;
    }
  }

  .btn-primary {
    background: linear-gradient(135deg, #409eff 0%, #66b1ff 100%);
    color: #fff;
  }

  .btn-danger {
    background: #f56c6c;
    color: #fff;
  }
}

.empty-box {
  padding: 40px 0;
}
</style>
